import { useContext, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Box, Stack, Tabs, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconDownload,
  IconAdjustmentsHorizontal,
  IconSettings,
} from "@tabler/icons-react";
import { INSIGHTS_ANALYSIS_SECTIONS } from "@congrs/schemas";

import { AuthContext } from "../services/context";
import { getCurrentWorkspace } from "../services/userConfig";
import { useSetActionButtons } from "../services/ActionButtonsContext";
import { useGetAllFeatureFlags } from "../api/featureFlags";
import {
  useGetInsightsAnalysis,
  usePatchInsightsAnalysis,
} from "../api/insights";
import { printInsightsView } from "../utils/printInsightsView";
import "../components/Insights/insights.print.css";

import EventSummary from "../components/Insights/EventSummary";
import SectionSettingsDrawer from "../components/Insights/SectionSettingsDrawer";
import { SectionPartsContext } from "../components/Insights/SectionShell";
import PrintHeader from "../components/Insights/PrintHeader";
import {
  GROUP_LABELS,
  PART_LABELS,
  SECTION_LABELS,
} from "../components/Insights/sectionMeta";

import AttendeeAccess from "../components/Insights/sections/AttendeeAccess";
import AgendaUsage from "../components/Insights/sections/AgendaUsage";
import ProfileViews from "../components/Insights/sections/ProfileViews";
import PagesUsage from "../components/Insights/sections/PagesUsage";
import Notifications from "../components/Insights/sections/Notifications";
import AppAdoption from "../components/Insights/sections/AppAdoption";
import QrCheckIn from "../components/Insights/sections/QrCheckIn";
import InviteFunnel from "../components/Insights/sections/InviteFunnel";
import Registrations from "../components/Insights/sections/Registrations";
import PollsAnalysis from "../components/Insights/sections/PollsAnalysis";
import SurveysAnalysis from "../components/Insights/sections/SurveysAnalysis";
import Giveaway from "../components/Insights/sections/Giveaway";
import InterestsTags from "../components/Insights/sections/InterestsTags";
import EngagementSegmentation from "../components/Insights/sections/EngagementSegmentation";
import Compliance from "../components/Insights/sections/Compliance";

const SECTION_COMPONENTS = {
  attendeeAccess: AttendeeAccess,
  agendaUsage: AgendaUsage,
  profileViews: ProfileViews,
  pagesUsage: PagesUsage,
  notifications: Notifications,
  appAdoption: AppAdoption,
  qrCheckIn: QrCheckIn,
  inviteFunnel: InviteFunnel,
  registrations: Registrations,
  pollsAnalysis: PollsAnalysis,
  surveysAnalysis: SurveysAnalysis,
  giveaway: Giveaway,
  interestsTags: InterestsTags,
  engagementSegmentation: EngagementSegmentation,
  compliance: Compliance,
};

// Combined height of the fixed TopHeader + AppShell header (see AppShell.jsx).
const APP_HEADER_OFFSET = 100;

function Insights() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);
  const eventId = currentWorkspace?.eventId;
  const isSysAdmin = user?.role === "sysAdmin";

  const [searchParams, setSearchParams] = useSearchParams();

  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false);

  const actions = useMemo(
    () => [
      ...(isSysAdmin
        ? [
            {
              label: "Customize sections",
              icon: <IconSettings />,
              onClick: openDrawer,
            },
            {
              label: "App feature usage weights",
              icon: <IconAdjustmentsHorizontal />,
              onClick: () => navigate("/app/insights/app-usage-weights"),
            },
          ]
        : []),
      {
        label: "Download PDF",
        icon: <IconDownload />,
        onClick: printInsightsView,
      },
    ],
    [isSysAdmin, navigate, openDrawer]
  );

  useSetActionButtons(actions);

  const { data: analysis } = useGetInsightsAnalysis(eventId);
  const { mutate: patchAnalysis, isPending: isPatchingEnabled } =
    usePatchInsightsAnalysis(eventId);

  const { data: flags } = useGetAllFeatureFlags({ eventId });
  const enabledFlagSet = useMemo(
    () =>
      new Set(
        (Array.isArray(flags) ? flags : [])
          .filter((f) => f.enabled)
          .map((f) => f.featureName)
      ),
    [flags]
  );

  const availableSections = useMemo(
    () =>
      INSIGHTS_ANALYSIS_SECTIONS.filter(
        (s) => !s.featureFlag || enabledFlagSet.has(s.featureFlag)
      ),
    [enabledFlagSet]
  );

  const enabledKeys = useMemo(() => {
    const set = new Set();
    for (const s of availableSections) {
      if (analysis?.sections?.[s.key]?.isEnabled !== false) set.add(s.key);
    }
    return set;
  }, [availableSections, analysis]);

  const queryClient = useQueryClient();
  const analysisQueryKey = ["insights", "analysis", eventId];

  const patchSection = (key, patch) => {
    queryClient.cancelQueries({ queryKey: analysisQueryKey });
    queryClient.setQueryData(analysisQueryKey, (prev) => {
      if (!prev) return prev;
      const section = prev.sections?.[key] ?? {};
      return {
        ...prev,
        sections: {
          ...prev.sections,
          [key]: {
            ...section,
            ...patch,
            parts: { ...section.parts, ...patch.parts },
          },
        },
      };
    });
    patchAnalysis(
      { sections: { [key]: patch } },
      {
        onError: () =>
          queryClient.invalidateQueries({ queryKey: analysisQueryKey }),
      }
    );
  };

  const toggle = (key, on) => patchSection(key, { isEnabled: on });

  const isPartShown = (key, part) =>
    analysis?.sections?.[key]?.parts?.[part] !== false;

  const togglePart = (key, part, on) =>
    patchSection(key, { parts: { [part]: on } });

  // All available sections per group (for the settings drawer).
  const groupedSections = useMemo(() => {
    const out = {};
    for (const s of availableSections) {
      const item = {
        key: s.key,
        label: SECTION_LABELS[s.key] ?? s.key,
        parts: s.parts.map((part) => ({
          key: part,
          label: PART_LABELS[s.key]?.[part] ?? part,
        })),
      };
      (out[s.group] ||= []).push(item);
    }
    return out;
  }, [availableSections]);

  // Enabled, component-backed sections per group (for tabs and rendering).
  const visibleGroups = useMemo(() => {
    const out = [];
    for (const s of availableSections) {
      const parts = analysis?.sections?.[s.key]?.parts;
      const allPartsHidden =
        s.parts.length > 0 && s.parts.every((part) => parts?.[part] === false);
      if (
        !enabledKeys.has(s.key) ||
        !SECTION_COMPONENTS[s.key] ||
        allPartsHidden
      )
        continue;
      const item = { key: s.key, label: SECTION_LABELS[s.key] ?? s.key };
      const group = out.find((g) => g.group === s.group);
      if (group) group.sections.push(item);
      else out.push({ group: s.group, sections: [item] });
    }
    return out;
  }, [availableSections, enabledKeys, analysis]);

  const tabParam = searchParams.get("tab");
  const activeTab = visibleGroups.some((g) => g.group === tabParam)
    ? tabParam
    : visibleGroups[0]?.group;

  const handleTabChange = (next) => {
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev);
      out.set("tab", next);
      return out;
    });
    window.scrollTo({ top: 0 });
  };

  return (
    <Stack gap="md" className="insights-print-root">
      <PrintHeader eventId={eventId} />

      {/* Sticky group tabs. Negative horizontal margins extend the background
          over AppShell.Main's padding gutters so content doesn't peek through
          while scrolling; the tab list's own border is the divider. */}
      {visibleGroups.length > 1 && (
        <Box
          className="insights-print-hide insights-sticky-header"
          style={{
            position: "sticky",
            top: APP_HEADER_OFFSET,
            zIndex: 10,
            background: "var(--mantine-color-body)",
            marginInline: "calc(var(--mantine-spacing-md) * -1)",
            paddingInline: "var(--mantine-spacing-md)",
            paddingTop: "var(--mantine-spacing-xs)",
          }}
        >
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tabs.List>
              {visibleGroups.map((g) => (
                <Tabs.Tab
                  key={g.group}
                  value={g.group}
                  rightSection={
                    <Badge
                      size="sm"
                      variant="light"
                      color={g.group === activeTab ? "blue" : "gray"}
                    >
                      {g.sections.length}
                    </Badge>
                  }
                >
                  {GROUP_LABELS[g.group] ?? g.group}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        </Box>
      )}

      <EventSummary eventId={eventId} />

      {/* Inactive groups stay mounted at full width (height 0, clipped) so
          their charts keep real dimensions and the PDF export can print
          every section, not just the active tab. */}
      {visibleGroups.map((g) => {
        const isActive = g.group === activeTab;
        return (
          <div
            key={g.group}
            className="insights-group"
            style={
              isActive
                ? undefined
                : {
                    height: 0,
                    overflow: "hidden",
                    // Cancel the parent Stack's gap so collapsed groups
                    // don't add phantom spacing between visible siblings.
                    marginTop: "calc(var(--mantine-spacing-md) * -1)",
                  }
            }
          >
            <Title order={2} className="insights-print-only" mb="sm">
              {GROUP_LABELS[g.group] ?? g.group}
            </Title>
            <Stack gap="lg">
              {g.sections.map((s) => {
                const Component = SECTION_COMPONENTS[s.key];
                return (
                  <SectionPartsContext.Provider
                    key={s.key}
                    value={analysis?.sections?.[s.key]?.parts}
                  >
                    <Component eventId={eventId} />
                  </SectionPartsContext.Provider>
                );
              })}
            </Stack>
          </div>
        );
      })}

      {isSysAdmin && (
        <SectionSettingsDrawer
          opened={drawerOpened}
          onClose={closeDrawer}
          groupedSections={groupedSections}
          enabledKeys={enabledKeys}
          onToggle={toggle}
          isPartShown={isPartShown}
          onTogglePart={togglePart}
          isAnalysisEnabled={Boolean(analysis?.isEnabled)}
          isPatchingEnabled={isPatchingEnabled}
          onToggleAnalysisEnabled={(value) =>
            patchAnalysis({ isEnabled: value })
          }
        />
      )}
    </Stack>
  );
}

export default Insights;
